import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { iRegisterViewPayload, ViewKind } from './campaign';
import { postMostraSeuAprovaView } from './queries';
import { QUERY_KEY } from './useCampaignState';

/**
 * Pede ao servidor autorização para exibir um modal e registra a exibição.
 *
 * O servidor reavalia janela, status e prazo, aloca o slot e devolve o estado
 * atualizado — por isso a chamada vem ANTES de abrir o modal. Um `false` aqui
 * significa que o modal não deve aparecer: ou a campanha virou, ou o aluno
 * enviou o vídeo, ou outra aba consumiu o slot primeiro.
 *
 * Nunca lança: falhar em exibir um modal de campanha não pode quebrar a tela em
 * que o aluno está.
 */
function useRegisterView() {
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: (payload: iRegisterViewPayload) =>
      postMostraSeuAprovaView(payload),
  });

  const registerView = useCallback(
    async (
      payload: iRegisterViewPayload
    ): Promise<{ registered: boolean; kind: ViewKind | null }> => {
      try {
        const { registered, kind, state } = await mutateAsync(payload);

        // O servidor já devolveu o estado novo: aproveitar evita uma segunda
        // requisição e mantém as abas consistentes com o que ele decidiu.
        queryClient.setQueryData(QUERY_KEY, state);

        return { registered, kind };
      } catch {
        return { registered: false, kind: null };
      }
    },
    [mutateAsync, queryClient]
  );

  return { registerView };
}

export { useRegisterView };
